/**
 * 画面表示用のデータ整形。
 *
 * 「この人はいくら払った / いくら受け取る」「この送金の説明文」といった
 * 表示のための計算をここに集約し、UI コンポーネントは並べるだけにする。
 */
import { EXPENSE_CATEGORY_ICON, EXPENSE_CATEGORY_LABELS, CONSTRAINT_LABELS, formatMoney, splitMemberIds, } from "@spli/core";
export function createMemberLookup(members) {
    const map = new Map(members.map((m) => [m.id, m]));
    return {
        nameOf: (memberId) => map.get(memberId)?.name ?? "(削除済み)",
        byId: (memberId) => map.get(memberId),
    };
}
export function toBalanceRows(balances, members, currency) {
    const lookup = createMemberLookup(members);
    return balances.map((balance) => ({
        ...balance,
        name: lookup.nameOf(balance.memberId),
        netLabel: formatMoney(Math.abs(balance.net), currency),
        paidLabel: formatMoney(balance.paid, currency),
        owedLabel: formatMoney(balance.owed, currency),
        status: balance.net > 0 ? "receive" : balance.net < 0 ? "pay" : "even",
    }));
}
export function toTransferRows(transfers, members, currency) {
    const lookup = createMemberLookup(members);
    return transfers.map((transfer, index) => ({
        key: `${transfer.fromMemberId}-${transfer.toMemberId}-${index}`,
        fromName: lookup.nameOf(transfer.fromMemberId),
        toName: lookup.nameOf(transfer.toMemberId),
        amount: transfer.amount,
        amountLabel: formatMoney(transfer.amount, currency),
        fromMemberId: transfer.fromMemberId,
        toMemberId: transfer.toMemberId,
        note: describeTransfer(transfer, lookup, currency),
        isRelay: transfer.kind === "relay",
    }));
}
function describeTransfer(transfer, lookup, currency) {
    // 全額が中継分 = 経路の一部なので、どこを通るかを示す
    if (transfer.kind === "relay" && transfer.via) {
        const path = transfer.via.map((id) => lookup.nameOf(id)).join(" → ");
        return `直接の送金を避ける設定のため ${path} の順で精算します`;
    }
    /*
     * 一部だけが中継分のケース。
     * 受取人にとっては「自分の受取 + 預かって渡す分」なので、
     * いくらを誰に渡すことになるのかを書いておく。
     */
    if (transfer.relayedAmount > 0) {
        const amount = formatMoney(transfer.relayedAmount, currency);
        const finalTo = transfer.via?.at(-1);
        const destination = finalTo && finalTo !== transfer.toMemberId ? `${lookup.nameOf(finalTo)} へ` : "別の人へ";
        return `うち ${amount} は ${lookup.nameOf(transfer.toMemberId)} から ${destination}渡してもらう分です`;
    }
    return transfer.reason;
}
export function toExpenseRows(expenses, members, currency) {
    const lookup = createMemberLookup(members);
    return expenses.map((expense) => ({
        id: expense.id,
        title: expense.title,
        icon: EXPENSE_CATEGORY_ICON[expense.category],
        categoryLabel: EXPENSE_CATEGORY_LABELS[expense.category],
        amountLabel: formatMoney(expense.amount, currency),
        occurredOn: expense.occurredOn,
        payerNames: expense.payers
            .filter((p) => p.amount > 0)
            .map((p) => lookup.nameOf(p.memberId))
            .join("・"),
        participantCount: splitMemberIds(expense.split).length,
        note: expense.note,
    }));
}
/** 精算結果の要約テキスト (共有用) */
export function buildShareText(group, settlement) {
    const lookup = createMemberLookup(group.members);
    const lines = [`【${group.name}】の精算結果 (スプリ)`];
    if (settlement.transfers.length === 0) {
        lines.push("精算は完了しています 🎉");
    }
    else {
        for (const transfer of settlement.transfers) {
            lines.push(`・${lookup.nameOf(transfer.fromMemberId)} → ${lookup.nameOf(transfer.toMemberId)} ${formatMoney(transfer.amount, group.currency)}`);
        }
    }
    for (const cover of settlement.covers) {
        lines.push(`※ ${lookup.nameOf(cover.coveredId)} の分 ${formatMoney(cover.amount, group.currency)} は ` +
            `${lookup.nameOf(cover.covererId)} が負担しています`);
    }
    return lines.join("\n");
}
export function toGroupStats(group) {
    const totalAmount = group.expenses.reduce((acc, e) => acc + e.amount, 0);
    const memberCount = Math.max(1, group.members.length);
    return {
        totalAmount,
        totalLabel: formatMoney(totalAmount, group.currency),
        expenseCount: group.expenses.length,
        memberCount: group.members.length,
        perPersonLabel: formatMoney(Math.round(totalAmount / memberCount), group.currency),
    };
}
export const constraintLabel = (kind) => CONSTRAINT_LABELS[kind];
/**
 * メンバーを削除できるかを、サーバーに問い合わせる前に判定する。
 *
 * 支出や精算に登場しているメンバーを消すと履歴の整合性が壊れるため、
 * サーバーは 400 を返す。ボタンを押してからエラーを見せるのではなく、
 * 押す前に「なぜ消せないか」と「どうすれば消せるか」を出すために使う。
 * 判定条件はサーバー (group-service.ts の deleteMember) と同じ。
 */
export function memberRemovability(group, memberId) {
    const expenseCount = group.expenses.filter((expense) => expense.payers.some((payer) => payer.memberId === memberId) ||
        splitMemberIds(expense.split).includes(memberId)).length;
    const settlementCount = group.settlements.filter((payment) => payment.fromMemberId === memberId || payment.toMemberId === memberId).length;
    if (expenseCount === 0 && settlementCount === 0) {
        return { expenseCount, settlementCount, canRemove: true, reason: null };
    }
    const parts = [
        expenseCount > 0 ? `支出 ${expenseCount} 件` : null,
        settlementCount > 0 ? `精算の記録 ${settlementCount} 件` : null,
    ].filter((part) => part !== null);
    return {
        expenseCount,
        settlementCount,
        canRemove: false,
        reason: `${parts.join(" と ")}に登場しているため削除できません`,
    };
}
/** ログインユーザーが「自分」として紐づけているメンバーを探す */
export function findMyMember(members, userId) {
    if (!userId)
        return null;
    return members.find((m) => m.userId === userId) ?? null;
}
/** 受取先を 1 行の文字列にする (コピー用) */
export function formatPayoutAccount(account) {
    if (account.kind === "bank") {
        const parts = [
            account.bankName,
            account.branchName,
            account.accountType === "checking" ? "当座" : account.accountType === "ordinary" ? "普通" : null,
            account.accountNumber,
            account.accountHolder,
        ].filter((v) => Boolean(v));
        return parts.join(" ");
    }
    const label = account.kind === "paypay" ? "PayPay" : "受取先";
    return [label, account.handle].filter((v) => Boolean(v)).join(" ");
}
//# sourceMappingURL=view-model.js.map